import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Navigation from '../../components/Navigation/Navigation';
import { useChurch } from '../../contexts/ChurchContext';
import {
  SeasonData, fetchSeason, fetchMusicianSuggestions,
} from '../../services/seasonViewService';
import { buildRows, SundayRow, Placement, shortDate } from './model';
import { OrganLane, PlayingLane } from './Lanes';
import AtKeyboard from './AtKeyboard';
import SundayDetail from './SundayDetail';
import PrintSheets from './PrintSheets';
import { SEASON_VAR, STATUS, StatusKey, badgeFor } from './tokens';
import './SeasonView.css';

type View = 'season' | 'sunday' | 'print';

const EMPTY: SeasonData = { services: [], selections: [], spanishSelections: [], organ: [], musicians: [] };

/** The window starts at this week's Sunday and runs forward N weeks. */
function windowFor(weeks: number): { start: string; end: string } {
  const now = new Date();
  now.setHours(12, 0, 0, 0);
  const start = new Date(now);
  start.setDate(start.getDate() - start.getDay());
  const end = new Date(start);
  end.setDate(end.getDate() + weeks * 7);
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return { start: iso(start), end: iso(end) };
}

export interface Suggestion { name: string; instrument: string | null }

const SeasonViewPage: React.FC = () => {
  const { userChurches } = useChurch();
  const [view, setView] = useState<View>('season');
  const [weeks, setWeeks] = useState(13);
  const [activeDate, setActiveDate] = useState<string | null>(null);
  const [data, setData] = useState<SeasonData>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);

  // Both the 10:00 English and the 12:30 Spanish, whatever churches they sit under.
  const churchIds = useMemo(() => userChurches.map(c => c.id), [userChurches]);

  const load = useCallback(async () => {
    const { start, end } = windowFor(weeks);
    setError(null);
    try {
      setData(await fetchSeason(start, end, churchIds.length ? churchIds : undefined));
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Could not load the season.');
    } finally {
      setLoading(false);
    }
  }, [weeks, churchIds]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { fetchMusicianSuggestions().then(setSuggestions).catch(() => {}); }, [data]);

  const rows = useMemo(() => buildRows(data), [data]);
  const active = useMemo(
    () => rows.find(r => r.date === activeDate) || rows[0] || null,
    [rows, activeDate]
  );

  const openSunday = (date: string) => { setActiveDate(date); setView('sunday'); };

  const spanLabel = rows.length
    ? `${shortDate(rows[0].date)} — ${shortDate(rows[rows.length - 1].date)}`
    : '';
  const dayLabel = rows.length
    ? `${rows[0].day} through ${rows[rows.length - 1].day}`
    : '';

  return (
    <>
      <Navigation />
      <div className="sv-root">
        <div className="sv-head">
          <div style={{ flex: 1, minWidth: 280 }}>
            <div className="sv-eyebrow">St Luke's ~ San Lucas · Music</div>
            <h1 className="sv-title">
              {spanLabel}
              {dayLabel && <span>&nbsp;&nbsp;·&nbsp;&nbsp;{dayLabel}</span>}
            </h1>
          </div>
          <div className="sv-btnrow cl-view-tabs">
            {(['season', 'sunday', 'print'] as View[]).map(k => (
              <button key={k} className={`sv-btn${view === k ? ' sv-on' : ''}`} onClick={() => setView(k)}>
                {k[0].toUpperCase() + k.slice(1)}
              </button>
            ))}
          </div>
          <div className="sv-btnrow sv-divided">
            {[6, 13, 26].map(w => (
              <button key={w} className={`sv-btn sv-mono${weeks === w ? ' sv-on' : ''}`} onClick={() => setWeeks(w)}>
                {w} wk
              </button>
            ))}
          </div>
        </div>

        {loading && <div className="sv-printnote">Loading…</div>}
        {error && <div className="sv-printnote sv-error">{error}</div>}

        {!loading && !error && view === 'season' && (
          <SeasonGrid rows={rows} suggestions={suggestions} onOpen={openSunday} onChanged={load} />
        )}
        {!loading && !error && view === 'sunday' && active && (
          <SundayDetail
            rows={rows}
            row={active}
            suggestions={suggestions}
            onPick={setActiveDate}
            onChanged={load}
          />
        )}
        {!loading && !error && view === 'print' && <PrintSheets rows={rows} />}
      </div>
    </>
  );
};

/* ------------------------------------------------------------------ grid -- */

const SeasonGrid: React.FC<{
  rows: SundayRow[];
  suggestions: Suggestion[];
  onOpen: (d: string) => void;
  onChanged: () => void;
}> = ({ rows, suggestions, onOpen, onChanged }) => (
  <div className="sv-body">
    <div className="sv-standing">
      <div>Weekly · Choir Tue 6:30 · Instrumentalists Wed 7:15{' '}
        <span className="sv-faint">— constant through the season, not shown per week</span>
      </div>
      <div className="sv-legend">
        {(['asked', 'accepted', 'confirmed', 'declined'] as StatusKey[]).map(k => (
          <span key={k}>
            <span style={{ color: STATUS[k].color }}>{STATUS[k].mark}</span> {STATUS[k].label}
          </span>
        ))}
      </div>
    </div>

    <div className="sv-colhead">
      <div />
      <div>Day</div>
      <div className="sv-musichead">
        <span>10:00 English</span>
        <span>12:30 Español</span>
      </div>
      <div>Repertoire</div>
      <div>Playing</div>
    </div>

    {rows.map(r => (
      <div key={r.date} className={`sv-grid cl-season-row${r.isFeast ? ' sv-feast' : ''}`}>
        <div
          className={`cl-spine${r.gap ? ' cl-spine--gap' : ''}`}
          style={{ ['--cl-spine-color' as any]: SEASON_VAR[r.season] }}
          title={r.gap
            ? 'The spine is broken here: no offertory music of any kind this Sunday'
            : 'Season colour'}
        />

        <div className="sv-daycell">
          <div className="sv-dateline">{r.dateLine}</div>
          <div className={`sv-dayname${r.isFeast ? ' sv-feastname' : ''}`}>{r.day}</div>
          <div className="sv-weight cl-weight-bar">
            <div className="sv-weight-track">
              <div className="sv-weight-fill" style={{ width: `${Math.min(100, Math.round((r.decided / 10) * 100))}%` }} />
            </div>
            <span className="sv-weight-label">{r.blank ? '—' : `${r.decided} set`}</span>
          </div>
          <button className="sv-open" onClick={() => onOpen(r.date)}>Open</button>
        </div>

        {/* Slot label, then the two services side by side — one line per slot,
            either side may be blank. */}
        <div className="sv-musiccell">
          {r.placements.length === 0
            ? <div className="sv-blank">
                Nothing chosen yet.
                {r.ideas.length ? ` ${r.ideas.length} parked on the Sunday page.` : ''}
              </div>
            : r.placements.map(p => <PlacementLine key={p.key} p={p} onChanged={onChanged} />)}
        </div>

        <div className="sv-repcell">
          <OrganLane
            serviceId={r.english?.service_id ?? r.spanish?.service_id ?? null}
            works={r.organ}
            onChanged={onChanged}
          />
        </div>

        <div className="sv-peoplecell">
          <PlayingLane
            serviceId={r.english?.service_id ?? r.spanish?.service_id ?? null}
            people={r.people}
            suggestions={suggestions}
            onChanged={onChanged}
          />
        </div>
      </div>
    ))}

    <div className="sv-footnote">
      The spine runs green through the season after Pentecost, gold at feasts, navy from Advent I.
      Where it breaks, that Sunday has no offertory music of any kind.
    </div>
  </div>
);

export const PlacementLine: React.FC<{ p: Placement; onChanged: () => void }> = ({ p, onChanged }) => {
  const badge = p.recurring ? null : badgeFor(p.enPerformanceType);
  return (
    <div className={`sv-place${p.recurring ? ' sv-recurring' : ''}`}>
      <div className="sv-slot">{p.slot}</div>

      <div className="sv-lang">
        {p.en && <span className="sv-title-en">{p.en}</span>}
        {badge && <span className={`sv-badge ${badge.className}`}>{badge.label}</span>}
        {p.enSelectionId && (
          <AtKeyboard selectionId={p.enSelectionId} on={p.enAtOrgan} onChanged={onChanged} />
        )}
        {p.en && !p.enHasFile && <span className="sv-nofile" title="no sheet music on file">◇</span>}
        {p.enComposer && !p.recurring && <span className="sv-composer">{p.enComposer}</span>}
        {p.enNotes && <span className="sv-note">{p.enNotes}</span>}
      </div>

      <div className="sv-lang">
        {p.es && <span className="sv-title-es">{p.es}</span>}
      </div>
    </div>
  );
};

export default SeasonViewPage;
